// ABOUTME: Settings page "Onboarding": progress card with step count, chapter status tiles, a pill
// ABOUTME: filter, the chapter card column and the detail pane of the selected chapter.

import AutorenewIcon from "@mui/icons-material/Autorenew";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { Box, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ShPillFilter from "../../../../components/theme/buttons/ShPillFilter";
import StatCard from "../../../../components/theme/cards/StatCard";
import StatCardRow from "../../../../components/theme/cards/StatCardRow";
import Heading from "../../../../components/theme/text/Heading";
import Subheading from "../../../../components/theme/text/Subheading";
import Text from "../../../../components/theme/text/Text";
import { useTopbar } from "../../../../contexts/TopbarProvider";
import { useGetOnboardingQuery } from "../../../../newapi/warehouse/onboardingSlice";
import { tokens } from "../../../../theme";
import ChapterCard, { panelSx } from "./views/ChapterCard";
import StepDetail from "./views/StepDetail";

const FILTERS = [
    { value: "all", label: "onboarding_tab_all" },
    { value: "busy", label: "onboarding_busy" },
    { value: "open", label: "onboarding_not_started" },
    { value: "finished", label: "onboarding_finished" },
];

// The three chapter counters, drawn as the filled stat cards the totals panels use.
const STAT_CARDS = [
    { key: "finished", label: "onboarding_finished", color: "green", icon: <TaskAltIcon className="logo" /> },
    { key: "busy", label: "onboarding_busy", color: "blue", icon: <AutorenewIcon className="logo" /> },
    { key: "open", label: "onboarding_not_started", color: "orange", icon: <PlayCircleOutlineIcon className="logo" /> },
];

const Onboarding = () => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { showExtraTitle, clearExtraTitle } = useTopbar();

    const { data, refetch } = useGetOnboardingQuery();
    const [filter, setFilter] = useState("all");
    const [selectedChapterKey, setSelectedChapterKey] = useState(null);

    useEffect(() => {
        showExtraTitle(t("onboarding"));

        return () => {
            clearExtraTitle();
        };
    }, [showExtraTitle, clearExtraTitle, t, i18n.language]);

    const chapters = data?.chapters ?? [];
    const summary = data?.summary ?? { finished: 0, busy: 0, open: 0, total: 0, steps: { handled: 0, total: 0 } };
    const steps = summary.steps ?? { handled: 0, total: 0 };
    const visibleChapters = chapters.filter((chapter) => filter === "all" || chapter.status === filter);
    const selectedChapter = chapters.find((chapter) => chapter.key === selectedChapterKey);
    const progress = steps.total > 0 ? Math.round((steps.handled / steps.total) * 100) : 0;

    const isDark = theme.palette.mode === "dark";
    const accent = isDark ? colors.blue[400] : colors.primary[400];
    const panel = panelSx(theme, colors);

    return (
        <Box
            sx={{
                width: 1,
                minHeight: 1,
                background: colors.bg["tertiary"],
                paddingTop: 9,
                paddingX: 4,
                paddingBottom: 4,
                display: "flex",
                flexDirection: "column",
                gap: 3,
                "@media screen and (max-width: 47.9375em)": {
                    paddingX: 2,
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    gap: 3,
                    alignItems: "stretch",
                    "@media screen and (max-width: 63.9375em)": {
                        flexDirection: "column",
                    },
                }}
            >
                <Box sx={{ ...panel, flex: 1.4, padding: 3, display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                            <Subheading>{t("onboarding_progress_title")}</Subheading>
                            <Text light>{t("onboarding_steps_completed", { done: steps.handled, total: steps.total })}</Text>
                        </Box>
                        <Heading color={accent} sx={{ fontSize: 28 }}>{`${progress}%`}</Heading>
                    </Box>
                    <Box sx={{ height: 8, borderRadius: 4, background: isDark ? colors.grey[400] : colors.grey[100], overflow: "hidden" }}>
                        <Box sx={{ height: 1, width: `${progress}%`, background: accent, transition: "width 300ms" }} />
                    </Box>
                    <Text light>{t("onboarding_progress_help")}</Text>
                </Box>

                <Box sx={{ ...panel, flex: 1, padding: 3, display: "flex", flexDirection: "column" }}>
                    <StatCardRow columns={3} sx={{ flex: 1, gridAutoRows: "minmax(0, 1fr)" }}>
                        {STAT_CARDS.map((card) => (
                            <StatCard
                                filled
                                key={card.key}
                                label={t(card.label)}
                                value={summary[card.key] ?? 0}
                                icon={card.icon}
                                color={card.color}
                                isLoading={!data}
                            />
                        ))}
                    </StatCardRow>
                </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
                <Subheading>{t("onboarding_chapters")}</Subheading>
                <ShPillFilter
                    solid
                    fullWidth={false}
                    options={FILTERS.map((option) => ({ value: option.value, label: t(option.label) }))}
                    value={filter}
                    onChange={setFilter}
                />
            </Box>

            <Box
                sx={{
                    display: "flex",
                    gap: 3,
                    alignItems: "flex-start",
                    "@media screen and (max-width: 63.9375em)": {
                        flexDirection: "column",
                    },
                }}
            >
                <Box
                    sx={{
                        width: 560,
                        flexShrink: 0,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        "@media screen and (max-width: 63.9375em)": {
                            width: 1,
                        },
                    }}
                >
                    {visibleChapters.map((chapter) => (
                        <ChapterCard
                            key={chapter.key}
                            chapter={chapter}
                            selected={chapter.key === selectedChapterKey}
                            onSelect={setSelectedChapterKey}
                        />
                    ))}
                    {data && visibleChapters.length === 0 && <Text light>{t("no_results")}</Text>}
                </Box>

                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        width: 1,
                        position: "sticky",
                        top: 88,
                        "@media screen and (max-width: 63.9375em)": {
                            position: "static",
                        },
                    }}
                >
                    {selectedChapter ? (
                        <StepDetail chapter={selectedChapter} onRefetch={refetch} />
                    ) : (
                        <Box sx={{ ...panel, padding: 3 }}>
                            <Text light>{t("onboarding_select_chapter")}</Text>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default Onboarding;
